import * as Sentry from '@sentry/vue'
import configs from '@/config'
import CrossDomainHttpClient from '@/services/crossDomainHttp'

const FETCH_AI_INSIGHTS_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
}

/**
 * Generates authentication headers for API requests
 * @returns {Object} Headers object with Authorization token if available
 */
const getAuthHeaders = () => {
  // Simple cookie getter for ahaToken
  const getCookie = (name) => {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    return parts.length === 2 ? parts.pop().split(';').shift() : null
  }

  const token = getCookie('ahaToken')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

const moduleState = {
  insights: [],
  status: FETCH_AI_INSIGHTS_STATUS.LOADING,
}

const getters = {
  insights: (state) => state.insights,
  status: (state) => state.status,
}

const mutations = {
  /* eslint-disable no-param-reassign */
  SET_INSIGHTS(state, insights) {
    state.insights = insights
  },
  SET_STATUS(state, status) {
    state.status = status
  },
  /* eslint-enable no-param-reassign */
}

const actions = {
  async fetchAIInsightsAction({ commit, rootState }, { presentationId, sessionId, forceRefresh = false }) {
    const userId = rootState.currentUser?.id
    const lang = rootState.presenterLocale || 'en'
    const storageKey = `${userId}-${presentationId}-${sessionId}-${lang}-genai-analysis`

    if (!forceRefresh) {
      const cachedInsights = localStorage.getItem(storageKey)
      if (cachedInsights) {
        try {
          const insightsArray = JSON.parse(cachedInsights)
          commit('SET_INSIGHTS', insightsArray)
          commit('SET_STATUS', FETCH_AI_INSIGHTS_STATUS.SUCCESS)
          return
        } catch (e) {
          localStorage.removeItem(storageKey)
        }
      }
    }

    try {
      commit('SET_STATUS', FETCH_AI_INSIGHTS_STATUS.LOADING)
      const url = `${configs.externalApis.aiInsightsBase}/${presentationId}/session/${sessionId}/`
      const response = await CrossDomainHttpClient.get(url, {}, getAuthHeaders())

      const insightsArray = response.body?.insights || []
      commit('SET_INSIGHTS', insightsArray)
      commit('SET_STATUS', FETCH_AI_INSIGHTS_STATUS.SUCCESS)

      // Store in local storage
      if (insightsArray.length > 0) {
        localStorage.setItem(storageKey, JSON.stringify(insightsArray))
      }
    } catch (error) {
      commit('SET_STATUS', FETCH_AI_INSIGHTS_STATUS.ERROR)
      Sentry.captureException(error, {
        tags: {
          feature: 'ai-insights',
          context: 'fetch-ai-insights-action',
        },
      })
      throw error
    }
  },
}

export default {
  namespaced: true,
  state: moduleState,
  getters,
  mutations,
  actions,
}
